/**
 * Composition Engine Dashboard - Unified Admin UI (Epic #568 Phase 6)
 *
 * Unified dashboard for the Data-Driven Composition Engine combining:
 * - Policy Template Library (Phase 1)
 * - Formula Registry (Phase 3)
 * - LLM Rule Suggestions (Phase 4)
 * - RBAC Controls (Phase 5)
 *
 * This dashboard allows administrators to:
 * - Browse and instantiate policy templates
 * - View and manage formulas (proven primitive combinations)
 * - Review and approve AI-generated rule suggestions
 * - Monitor composition health and anti-pattern detection
 */

import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Container,
  Divider,
  Grid,
  Group,
  List,
  Loader,
  Paper,
  Progress,
  RingProgress,
  ScrollArea,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconBook,
  IconBrain,
  IconCheck,
  IconChartBar,
  IconFlask,
  IconLayoutDashboard,
  IconRefresh,
  IconRobot,
  IconSettings,
  IconShield,
  IconSparkles,
  IconTemplate,
} from '@tabler/icons-react';
import React, { useState } from 'react';
import { useFormulas, type Formula, WarningSeverity } from '../hooks/useFormulas';
import { useSuggestionReview, type SuggestedRule } from '../hooks/useRuleSuggestions';
import { useAuth } from '../hooks/useAuth';
import { RuleSuggestionReviewer } from './RuleSuggestionReviewer';

// ============================================================================
// TYPES
// ============================================================================

interface CompositionEngineDashboardProps {
  tenantId: string;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Formula Card - Displays a single formula pattern
 */
const FormulaCard: React.FC<{ formula: Formula }> = ({ formula }) => {
  const successColor =
    formula.successRate >= 0.9 ? 'green' : formula.successRate >= 0.8 ? 'teal' : 'yellow';

  return (
    <Card shadow="sm" p="md" radius="md" withBorder>
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <ThemeIcon color="blue" variant="light" size="lg">
            <IconFlask size={18} />
          </ThemeIcon>
          <div>
            <Text fw={600}>{formula.name}</Text>
            <Badge size="xs" color="gray" variant="outline">
              {formula.discoveryMethod}
            </Badge>
          </div>
        </Group>
        <RingProgress
          size={50}
          thickness={4}
          roundCaps
          sections={[{ value: formula.successRate * 100, color: successColor }]}
          label={
            <Text size="xs" ta="center" fw={700}>
              {Math.round(formula.successRate * 100)}%
            </Text>
          }
        />
      </Group>

      <Text size="sm" c="dimmed" mb="sm">
        {formula.description}
      </Text>

      <Group gap="xs" mb="sm">
        {formula.primitives.map((primitive, idx) => (
          <Badge key={idx} size="xs" variant="light">
            {primitive}
          </Badge>
        ))}
      </Group>

      <Divider mb="sm" />

      <Group justify="space-between">
        <Text size="xs" c="dimmed">
          {formula.usageCount.toLocaleString()} workflows
        </Text>
        <Badge size="xs" color={successColor}>
          {Math.round(formula.successRate * 100)}% success
        </Badge>
      </Group>
    </Card>
  );
};

/**
 * Quick Stats Panel - Overview metrics
 */
const QuickStatsPanel: React.FC<{
  totalFormulas: number;
  pendingSuggestions: number;
  antiPatternWarnings: number;
  avgSuccessRate: number;
}> = ({ totalFormulas, pendingSuggestions, antiPatternWarnings, avgSuccessRate }) => {
  return (
    <SimpleGrid cols={{ base: 4 }}>
      <Paper p="md" withBorder>
        <Group>
          <ThemeIcon size="xl" color="blue" variant="light">
            <IconFlask size={24} />
          </ThemeIcon>
          <div>
            <Text size="xl" fw={700}>
              {totalFormulas}
            </Text>
            <Text size="xs" c="dimmed">
              Active Formulas
            </Text>
          </div>
        </Group>
      </Paper>

      <Paper p="md" withBorder>
        <Group>
          <ThemeIcon size="xl" color="violet" variant="light">
            <IconRobot size={24} />
          </ThemeIcon>
          <div>
            <Text size="xl" fw={700}>
              {pendingSuggestions}
            </Text>
            <Text size="xs" c="dimmed">
              Pending Suggestions
            </Text>
          </div>
        </Group>
      </Paper>

      <Paper p="md" withBorder>
        <Group>
          <ThemeIcon
            size="xl"
            color={antiPatternWarnings > 0 ? 'orange' : 'green'}
            variant="light"
          >
            <IconAlertTriangle size={24} />
          </ThemeIcon>
          <div>
            <Text size="xl" fw={700}>
              {antiPatternWarnings}
            </Text>
            <Text size="xs" c="dimmed">
              Anti-Pattern Warnings
            </Text>
          </div>
        </Group>
      </Paper>

      <Paper p="md" withBorder>
        <Group>
          <ThemeIcon size="xl" color="teal" variant="light">
            <IconChartBar size={24} />
          </ThemeIcon>
          <div>
            <Text size="xl" fw={700}>
              {Math.round(avgSuccessRate)}%
            </Text>
            <Text size="xs" c="dimmed">
              Avg Success Rate
            </Text>
          </div>
        </Group>
      </Paper>
    </SimpleGrid>
  );
};

/**
 * Formula Library Panel - Browse and view formulas
 */
const FormulaLibraryPanel: React.FC = () => {
  const { formulas, loading, error, refetch } = useFormulas();

  if (loading) {
    return (
      <Paper p="xl" style={{ textAlign: 'center' }}>
        <Loader size="lg" />
        <Text mt="md" c="dimmed">
          Loading formulas...
        </Text>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error loading formulas">
        {error.message}
      </Alert>
    );
  }

  if (formulas.length === 0) {
    return (
      <Paper p="xl" withBorder style={{ textAlign: 'center' }}>
        <IconFlask size={48} color="gray" />
        <Title order={4} mt="md">
          No Formulas Found
        </Title>
        <Text c="dimmed" mt="xs">
          Formulas are proven primitive combinations discovered through usage analysis
        </Text>
      </Paper>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={4}>Formula Library</Title>
        <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={() => refetch()}>
          Refresh
        </Button>
      </Group>

      <SimpleGrid cols={{ base: 2 }}>
        {formulas.map((formula) => (
          <FormulaCard key={formula.id} formula={formula} />
        ))}
      </SimpleGrid>
    </Stack>
  );
};

/**
 * Template Library Panel - Browse policy templates
 */
const TemplateLibraryPanel: React.FC = () => {
  // Mock data - in production this would come from useQuery
  const templates = [
    {
      id: 'high-confidence-auto-approve',
      name: 'High Confidence Auto-Approve',
      description: 'Automatically approve when AI confidence is above threshold',
      category: 'Approval',
      parameters: ['confidence_threshold'],
    },
    {
      id: 'low-confidence-expert-review',
      name: 'Low Confidence Expert Review',
      description: 'Route to expert review when confidence is below threshold',
      category: 'Escalation',
      parameters: ['confidence_threshold', 'expert_role'],
    },
    {
      id: 'life-critical-dual-control',
      name: 'Life-Critical Dual Control',
      description: 'Require two independent approvers for life-critical decisions',
      category: 'Compliance',
      parameters: ['criticality_level', 'min_approvers'],
    },
    {
      id: 'validation-violation-compliance',
      name: 'Validation Violation Compliance',
      description: 'Route to compliance review when validation violations detected',
      category: 'Compliance',
      parameters: ['violation_types'],
    },
    {
      id: 'hipaa-compliance',
      name: 'HIPAA Compliance',
      description: 'Healthcare-specific compliance rules for HIPAA requirements',
      category: 'Compliance',
      parameters: ['data_type', 'access_level'],
    },
    {
      id: 'sla-breach-escalation',
      name: 'SLA Breach Escalation',
      description: 'Escalate when SLA breach is imminent or occurred',
      category: 'Escalation',
      parameters: ['sla_threshold_minutes', 'escalation_level'],
    },
  ];

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={4}>Policy Templates</Title>
        <Badge>{templates.length} templates</Badge>
      </Group>

      <SimpleGrid cols={{ base: 2 }}>
        {templates.map((template) => (
          <Card key={template.id} shadow="sm" p="md" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Group gap="xs">
                <ThemeIcon color="indigo" variant="light" size="lg">
                  <IconTemplate size={18} />
                </ThemeIcon>
                <Text fw={600}>{template.name}</Text>
              </Group>
              <Badge color="blue" variant="light">
                {template.category}
              </Badge>
            </Group>

            <Text size="sm" c="dimmed" mb="sm">
              {template.description}
            </Text>

            <Text size="xs" fw={500} mb="xs">
              Parameters:
            </Text>
            <Group gap="xs">
              {template.parameters.map((param, idx) => (
                <Code key={idx}>
                  {param}
                </Code>
              ))}
            </Group>

            <Divider my="sm" />

            <Button variant="light" fullWidth size="xs">
              Instantiate Template
            </Button>
          </Card>
        ))}
      </SimpleGrid>
    </Stack>
  );
};

/**
 * Health Overview Panel - System health and anti-pattern detection
 */
const HealthOverviewPanel: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  // Mock health data - in production this would come from the backend
  const healthMetrics = {
    compositionHealth: 92,
    ruleEvaluationLatency: 12, // ms
    cacheHitRate: 99.2,
    activeRules: 47,
    antiPatterns: [
      {
        id: 'formula-1-warning',
        severity: 'MEDIUM',
        message: 'SLA without Notification in workflow "claims-processing"',
        suggestion: 'Add Notification primitive to enable deadline alerts',
      },
    ],
  };

  return (
    <Stack gap="md">
      <Title order={4}>System Health</Title>

      <SimpleGrid cols={{ base: 4 }}>
        <Paper p="sm" withBorder>
          <Text size="xs" c="dimmed" mb="xs">
            Composition Health
          </Text>
          <Progress
            value={healthMetrics.compositionHealth}
            color={healthMetrics.compositionHealth >= 90 ? 'green' : 'yellow'}
            size="lg"
          />
          <Text size="sm" ta="right" mt="xs">
            {healthMetrics.compositionHealth}%
          </Text>
        </Paper>

        <Paper p="sm" withBorder>
          <Text size="xs" c="dimmed" mb="xs">
            Evaluation Latency
          </Text>
          <Text size="xl" fw={700} c={healthMetrics.ruleEvaluationLatency < 50 ? 'green' : 'orange'}>
            {healthMetrics.ruleEvaluationLatency}ms
          </Text>
        </Paper>

        <Paper p="sm" withBorder>
          <Text size="xs" c="dimmed" mb="xs">
            Cache Hit Rate
          </Text>
          <Text size="xl" fw={700} c="teal">
            {healthMetrics.cacheHitRate}%
          </Text>
        </Paper>

        <Paper p="sm" withBorder>
          <Text size="xs" c="dimmed" mb="xs">
            Active Rules
          </Text>
          <Text size="xl" fw={700}>
            {healthMetrics.activeRules}
          </Text>
        </Paper>
      </SimpleGrid>

      {/* Anti-Pattern Warnings */}
      {healthMetrics.antiPatterns.length > 0 && (
        <Card withBorder p="md">
          <Group mb="sm">
            <IconAlertTriangle size={20} color="orange" />
            <Text fw={600}>Anti-Pattern Warnings</Text>
          </Group>

          <Stack gap="xs">
            {healthMetrics.antiPatterns.map((pattern) => (
              <Alert key={pattern.id} color="orange" variant="light" title={pattern.message}>
                <Text size="sm">{pattern.suggestion}</Text>
                <Button variant="light" size="xs" mt="sm">
                  Apply Fix
                </Button>
              </Alert>
            ))}
          </Stack>
        </Card>
      )}
    </Stack>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const CompositionEngineDashboard: React.FC<CompositionEngineDashboardProps> = ({
  tenantId,
}) => {
  const [activeTab, setActiveTab] = useState<string | null>('overview');
  const { currentUser } = useAuth();

  // Fetch data for overview stats
  const { formulas } = useFormulas();
  const { count: pendingSuggestions } = useSuggestionReview(tenantId);

  // Calculate stats
  const totalFormulas = formulas.length;
  const avgSuccessRate =
    formulas.length > 0
      ? (formulas.reduce((sum, f) => sum + f.successRate, 0) / formulas.length) * 100
      : 0;

  // Check admin permissions
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'policy_admin';

  const handleRuleAccepted = (rule: SuggestedRule) => {
    notifications.show({
      title: 'Rule Created',
      message: `"${rule.ruleName}" has been added to your active rules`,
      color: 'green',
      icon: <IconCheck size={16} />,
    });
  };

  const handleRuleRejected = (rule: SuggestedRule) => {
    // Notification already shown in RuleSuggestionReviewer
  };

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="sm">
            <ThemeIcon size="xl" color="blue" variant="light">
              <IconBrain size={28} />
            </ThemeIcon>
            <div>
              <Title order={2}>Composition Engine</Title>
              <Text size="sm" c="dimmed">
                Data-driven workflow composition with AI-assisted rule creation
              </Text>
            </div>
          </Group>

          <Group gap="xs">
            <Badge color="green" variant="light" size="lg">
              Healthy
            </Badge>
            <Tooltip label="Epic #568">
              <Badge color="blue" variant="outline">
                v1.0
              </Badge>
            </Tooltip>
          </Group>
        </Group>

        {/* Quick Stats */}
        <QuickStatsPanel
          totalFormulas={totalFormulas}
          pendingSuggestions={pendingSuggestions}
          antiPatternWarnings={1} // Mock - would come from backend
          avgSuccessRate={avgSuccessRate}
        />

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? '')}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconLayoutDashboard size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="formulas" leftSection={<IconFlask size={16} />}>
              Formulas ({totalFormulas})
            </Tabs.Tab>
            <Tabs.Tab value="templates" leftSection={<IconTemplate size={16} />}>
              Templates
            </Tabs.Tab>
            <Tabs.Tab value="suggestions" leftSection={<IconRobot size={16} />}>
              AI Suggestions
              {pendingSuggestions > 0 && (
                <Badge ml="xs" size="xs" color="red" variant="filled">
                  {pendingSuggestions}
                </Badge>
              )}
            </Tabs.Tab>
            <Tabs.Tab value="health" leftSection={<IconChartBar size={16} />}>
              Health
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <Grid>
              <Grid.Col span={8}>
                <Stack gap="md">
                  {/* Quick Actions */}
                  <Card withBorder p="md">
                    <Title order={5} mb="md">
                      Quick Actions
                    </Title>
                    <SimpleGrid cols={{ base: 3 }}>
                      <Button
                        variant="light"
                        leftSection={<IconSparkles size={16} />}
                        onClick={() => setActiveTab('suggestions')}
                      >
                        Generate Suggestions
                      </Button>
                      <Button
                        variant="light"
                        leftSection={<IconTemplate size={16} />}
                        onClick={() => setActiveTab('templates')}
                      >
                        Browse Templates
                      </Button>
                      <Button
                        variant="light"
                        leftSection={<IconFlask size={16} />}
                        onClick={() => setActiveTab('formulas')}
                      >
                        View Formulas
                      </Button>
                    </SimpleGrid>
                  </Card>

                  {/* Recent Activity - Mock data */}
                  <Card withBorder p="md">
                    <Title order={5} mb="md">
                      Recent Activity
                    </Title>
                    <List spacing="sm" size="sm">
                      <List.Item
                        icon={
                          <ThemeIcon color="green" size={20} radius="xl">
                            <IconCheck size={12} />
                          </ThemeIcon>
                        }
                      >
                        Rule &quot;auto-approve-high-confidence&quot; accepted - 2 hours ago
                      </List.Item>
                      <List.Item
                        icon={
                          <ThemeIcon color="blue" size={20} radius="xl">
                            <IconSparkles size={12} />
                          </ThemeIcon>
                        }
                      >
                        5 new suggestions generated - 4 hours ago
                      </List.Item>
                      <List.Item
                        icon={
                          <ThemeIcon color="orange" size={20} radius="xl">
                            <IconAlertTriangle size={12} />
                          </ThemeIcon>
                        }
                      >
                        Anti-pattern detected in &quot;claims-processing&quot; - 6 hours ago
                      </List.Item>
                    </List>
                  </Card>
                </Stack>
              </Grid.Col>

              <Grid.Col span={4}>
                {/* Pending Suggestions Summary */}
                <Card withBorder p="md" h="100%">
                  <Group justify="space-between" mb="md">
                    <Title order={5}>Pending Review</Title>
                    <IconRobot size={20} color="gray" />
                  </Group>

                  {pendingSuggestions > 0 ? (
                    <Stack gap="sm">
                      <Text size="xl" fw={700} ta="center">
                        {pendingSuggestions}
                      </Text>
                      <Text size="sm" c="dimmed" ta="center">
                        AI suggestions awaiting review
                      </Text>
                      <Button
                        variant="filled"
                        fullWidth
                        onClick={() => setActiveTab('suggestions')}
                      >
                        Review Now
                      </Button>
                    </Stack>
                  ) : (
                    <Stack gap="sm" ta="center">
                      <IconCheck size={32} color="green" />
                      <Text size="sm" c="dimmed" ta="center">
                        All suggestions reviewed
                      </Text>
                    </Stack>
                  )}
                </Card>
              </Grid.Col>
            </Grid>
          </Tabs.Panel>

          <Tabs.Panel value="formulas" pt="md">
            <FormulaLibraryPanel />
          </Tabs.Panel>

          <Tabs.Panel value="templates" pt="md">
            <TemplateLibraryPanel />
          </Tabs.Panel>

          <Tabs.Panel value="suggestions" pt="md">
            {isAdmin ? (
              <RuleSuggestionReviewer
                tenantId={tenantId}
                onRuleAccepted={handleRuleAccepted}
                onRuleRejected={handleRuleRejected}
              />
            ) : (
              <Alert color="yellow" title="Admin Access Required">
                You need admin or policy_admin role to review and approve AI suggestions.
              </Alert>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="health" pt="md">
            <HealthOverviewPanel tenantId={tenantId} />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
};

export default CompositionEngineDashboard;
